import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Building2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

import { CampusTabs } from '../components/campus/CampusTabs';
import { OverviewTab, MembersTab } from '../components/campus/CampusOverview';
import { InviteTab } from '../components/campus/CampusInvite';
import { BillingTab } from '../components/campus/CampusBilling';
import { SsoTab } from '../components/campus/CampusSso';
import { LeaderboardTab, ReportsTab } from '../components/campus/CampusLeaderboardReports';
import { PlansView } from '../components/campus/CampusPlans';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CampusPage = () => {
  const [loading, setLoading] = useState(true);
  const [plans, setPlans] = useState([]);
  const [myCampus, setMyCampus] = useState(null);
  const [members, setMembers] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [inviteEmails, setInviteEmails] = useState('');
  const [inviting, setInviting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [leaderboard, setLeaderboard] = useState(null);
  const [billing, setBilling] = useState(null);
  const [ssoConfig, setSsoConfig] = useState(null);
  const [ssoForm, setSsoForm] = useState({
    sso_provider: 'saml', entity_id: '', sso_url: '', certificate: '', domain_restriction: '', auto_provision: true,
  });
  const [savingSso, setSavingSso] = useState(false);
  const [registering, setRegistering] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [paymentForm, setPaymentForm] = useState({ user_count: 10, billing_period: 'monthly', email: '' });
  const [processingPayment, setProcessingPayment] = useState(false);
  const [regForm, setRegForm] = useState({ name: '', institution: '', plan_id: '', admin_email: '' });
  const [testingSso, setTestingSso] = useState(false);
  const [ssoTestResult, setSsoTestResult] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [plansRes, campusRes] = await Promise.all([
        axios.get(`${API}/campus/plans`),
        axios.get(`${API}/campus/my-campus`),
      ]);
      setPlans(plansRes.data.plans || []);
      setMyCampus(campusRes.data);

      if (campusRes.data?.has_campus && campusRes.data?.role === 'admin') {
        const [membersRes, analyticsRes, lbRes, billingRes, ssoRes] = await Promise.all([
          axios.get(`${API}/campus/members`).catch(() => ({ data: null })),
          axios.get(`${API}/campus/analytics`).catch(() => ({ data: null })),
          axios.get(`${API}/campus/leaderboard`).catch(() => ({ data: null })),
          axios.get(`${API}/campus/billing`).catch(() => ({ data: null })),
          axios.get(`${API}/campus/sso/config`).catch(() => ({ data: null })),
        ]);
        setMembers(membersRes.data);
        setAnalytics(analyticsRes.data);
        setLeaderboard(lbRes.data);
        setBilling(billingRes.data);
        if (ssoRes.data) {
          setSsoConfig(ssoRes.data);
          if (ssoRes.data.config) {
            setSsoForm(ssoRes.data.config);
          }
        }
      }
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleRegister = async () => {
    if (!regForm.name || !regForm.institution || !regForm.plan_id || !regForm.admin_email) {
      toast.error('Fill all fields');
      return;
    }
    setRegistering(true);
    try {
      const res = await axios.post(`${API}/campus/register`, regForm);
      toast.success(res.data.message);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Registration failed');
    } finally {
      setRegistering(false);
    }
  };

  const handleInvite = async () => {
    const emails = inviteEmails.split(/[,\n]/).map(e => e.trim()).filter(Boolean);
    if (!emails.length) {
      toast.error('Enter at least one email');
      return;
    }
    setInviting(true);
    try {
      const res = await axios.post(`${API}/campus/invite`, { emails });
      toast.success(res.data.message);
      setInviteEmails('');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invite failed');
    } finally {
      setInviting(false);
    }
  };

  const handleCSVImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/campus/import-csv`, formData);
      toast.success(res.data.message);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'CSV import failed');
    } finally {
      setImporting(false);
      e.target.value = '';
    }
  };

  const handleExportReport = async (format = 'csv') => {
    try {
      const url = `${API}/campus/export-report?format=${format}`;
      if (format === 'csv') {
        const res = await axios.get(url, { responseType: 'blob' });
        const blobUrl = window.URL.createObjectURL(new Blob([res.data]));
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = `campus_report_${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
        window.URL.revokeObjectURL(blobUrl);
      } else {
        const res = await axios.get(url);
        const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
        const blobUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = `campus_report_${new Date().toISOString().slice(0, 10)}.json`;
        a.click();
        window.URL.revokeObjectURL(blobUrl);
      }
      toast.success('Report downloaded!');
    } catch (err) {
      toast.error('Failed to export report');
    }
  };

  const handleInitiatePayment = async () => {
    if (!paymentForm.email) {
      toast.error('Enter billing email');
      return;
    }
    setProcessingPayment(true);
    try {
      const res = await axios.post(`${API}/campus/initialize-payment`, {
        plan_id: myCampus?.campus?.plan_id,
        user_count: paymentForm.user_count,
        billing_period: paymentForm.billing_period,
        email: paymentForm.email,
      });
      if (res.data?.data?.authorization_url) {
        window.location.href = res.data.data.authorization_url;
      } else {
        toast.error('Payment URL not received');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Payment failed');
    } finally {
      setProcessingPayment(false);
    }
  };

  const handleSaveSso = async () => {
    setSavingSso(true);
    try {
      const res = await axios.put(`${API}/campus/sso/config`, ssoForm);
      toast.success(res.data.message);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save SSO config');
    } finally {
      setSavingSso(false);
    }
  };

  const handleTestSso = async () => {
    setTestingSso(true);
    setSsoTestResult(null);
    try {
      const res = await axios.post(`${API}/campus/sso/test`);
      setSsoTestResult(res.data);
      if (res.data.success) {
        toast.success('SSO configuration validated!');
      } else {
        toast.info(res.data.message);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Test failed');
    } finally {
      setTestingSso(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96" data-testid="campus-loading">
        <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
      </div>
    );
  }

  if (!myCampus?.has_campus) {
    return (
      <PlansView
        plans={plans}
        regForm={regForm}
        setRegForm={setRegForm}
        onRegister={handleRegister}
        registering={registering}
      />
    );
  }

  const campus = myCampus.campus;
  const plan = plans.find(p => p.id === campus.plan_id);
  const pricePerUser = plan?.price_per_user_monthly || 0;

  const renderActiveTab = () => {
    if (activeTab === 'overview') return <OverviewTab analytics={analytics} campus={campus} />;
    if (activeTab === 'members') return <MembersTab members={members} />;
    if (activeTab === 'invite') return (
      <InviteTab
        inviteEmails={inviteEmails}
        setInviteEmails={setInviteEmails}
        onInvite={handleInvite}
        inviting={inviting}
        onCSVImport={handleCSVImport}
        importing={importing}
      />
    );
    if (activeTab === 'billing') return (
      <BillingTab
        billing={billing}
        campus={campus}
        pricePerUser={pricePerUser}
        showPayment={showPayment}
        setShowPayment={setShowPayment}
        paymentForm={paymentForm}
        setPaymentForm={setPaymentForm}
        onInitiatePayment={handleInitiatePayment}
        processingPayment={processingPayment}
      />
    );
    if (activeTab === 'sso') return (
      <SsoTab
        ssoConfig={ssoConfig}
        ssoForm={ssoForm}
        setSsoForm={setSsoForm}
        onSave={handleSaveSso}
        savingSso={savingSso}
        onTest={handleTestSso}
        testingSso={testingSso}
        ssoTestResult={ssoTestResult}
        campus={campus}
      />
    );
    if (activeTab === 'leaderboard') return <LeaderboardTab leaderboard={leaderboard} />;
    if (activeTab === 'reports') return <ReportsTab onExport={handleExportReport} />;
    return null;
  };

  return (
    <div className="space-y-6" data-testid="campus-dashboard">
      <div>
        <h1 className="text-3xl sm:text-4xl font-bold text-[var(--isp-heading)] tracking-tight flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-indigo-500 flex items-center justify-center">
            <Building2 className="w-6 h-6 text-white" />
          </div>
          {campus.name}
        </h1>
        <p className="text-[var(--isp-body)] mt-1">{campus.institution} | {campus.plan_name}</p>
      </div>

      <CampusTabs activeTab={activeTab} onChange={setActiveTab} />
      {renderActiveTab()}
    </div>
  );
};

export default CampusPage;
